import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuthContext } from './context/AuthProvider';
import RouteFallback from './components/common/RouteFallback';
import ProtectedRoute from './components/common/ProtectedRoute';
import DashboardLayout from './layouts/DashboardLayout';
import { HOME_ROUTE } from './utils/redirect';

const SiopLayout = lazy(() => import('./pages/siop/SiopLayout'));
const HomePage = lazy(() => import('./pages/home/HomePage'));
const ForecastingPage = lazy(() => import('./pages/forecasting/ForecastingPage'));
const OverviewTab = lazy(() => import('./pages/siop/tabs/OverviewTab'));
const SubBrandTab = lazy(() => import('./pages/siop/tabs/SubBrandTab'));
const StdCostTab = lazy(() => import('./pages/siop/tabs/StdCostTab'));
const FieldGuideTab = lazy(() => import('./pages/siop/tabs/FieldGuideTab'));
const PulseBridgeTab = lazy(() => import('./pages/siop/tabs/PulseBridgeTab'));
const ProductsPulseTab = lazy(() => import('./pages/siop/tabs/ProductsPulseTab'));
const OrderRateTab = lazy(() => import('./pages/siop/tabs/OrderRateTab'));
const LookupTable = lazy(() => import('./components/siop/LookupTable'));
const Login = lazy(() => import('./pages/login/Login'));
const Settings = lazy(() => import('./pages/Settings/Settings'));

// By Category, By AOP Plan and Supply Chain Zones are no longer in any
// workspace (the tool shows what the prototype shows). Their pages
// (CategoryTab, AopPlanTab, ScZoneTab) stay on disk, unrouted; a bookmark lands
// on Overview with whatever ?region=… scope it carried. To bring one back,
// restore its lazy import and route here and add its tab in config/workspaces.js.
const ToOverview = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/siop/overview', search }} replace />;
};

const App = () => {
  const { authState, bootstrapping } = useAuthContext();

  // Hold ALL routing until the session probe settles. Rendering routes first
  // would flash the login page at a signed-in user reloading a deep link, and
  // then replace their URL with /login — losing where they were.
  if (bootstrapping) return <RouteFallback />;

  return (
    <div>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public. Already signed in? There is nothing to log into — go
              Home, so a stray /login never presents a second sign-in form. */}
          <Route
            path="/login"
            element={authState?.isLoggedIn ? <Navigate to={HOME_ROUTE} replace /> : <Login />}
          />

          <Route element={<ProtectedRoute />}>
            <Route element={<DashboardLayout />}>
              {/* Home — the entry point to every workspace. */}
              <Route path="/home" element={<HomePage />} />
              {/* Forecasting Analysis — its own workspace and filters, outside the
                  SIOP cockpit layout. Sample data until the DS API lands. */}
              <Route path="/forecasting" element={<ForecastingPage />} />
              {/* SIOP cockpit — filters + KPI cards live in the layout and stay
                  mounted across every page below (SiopLayout wraps <Outlet>). */}
              <Route path="/siop" element={<SiopLayout />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<OverviewTab />} />
                <Route path="category" element={<ToOverview />} />
                <Route path="aop-plan" element={<ToOverview />} />
                <Route path="sub-brand" element={<SubBrandTab />} />
                <Route path="sc-zones" element={<ToOverview />} />
                <Route path="std-cost" element={<StdCostTab />} />
                <Route path="lookup" element={<LookupTable />} />
                {/* Scorecard is hidden: the nav entry is gone, and a bookmarked
                    URL lands on Overview rather than a page nobody can navigate
                    back from. AccuracyScorecard.jsx and /api/v1/siop/scorecard
                    are untouched — restore the element below and the NAV_ITEMS
                    line in Navbar.jsx together. */}
                <Route path="scorecard" element={<Navigate to="../overview" replace />} />
                {/* The global guide, and one workspace's sections of it. */}
                <Route path="field-guide" element={<FieldGuideTab />} />
                <Route path="field-guide/:workspace" element={<FieldGuideTab />} />
                <Route path="pulse-bridge/*" element={<PulseBridgeTab />} />
                <Route path="products-pulse/*" element={<ProductsPulseTab />} />
                {/* Daily Pulse is temporarily hidden; keep its implementation on disk for restoration. */}
                <Route path="pulse-daily" element={<Navigate to="../pulse-bridge" replace />} />
                {/* Config, not a read-out: where the business enters X for both
                    Pulse pages. Sits inside the cockpit so it shares the header. */}
                <Route path="order-rate" element={<OrderRateTab />} />
              </Route>
              {/* Retired standalone pages — everything lives in the SIOP cockpit now. */}
              <Route path="/scorecard" element={<Navigate to="/siop/overview" replace />} />
              <Route path="/dashboard" element={<Navigate to="/siop/overview" replace />} />
              {/* Admin-only in practice: Settings renders nothing without the
                  USER_MGMT grant, which rbac.constants.js gives to ADMIN alone.
                  Was a redirect to the cockpit, which made the only user-admin
                  UI unreachable. */}
              <Route path="/settings" element={<Settings />} />
              <Route path="/" element={<Navigate to={HOME_ROUTE} replace />} />
              {/* Catch-all sits INSIDE the guard so an unknown path from a
                  signed-out visitor lands on /login, not on Home. */}
              <Route path="*" element={<Navigate to={HOME_ROUTE} replace />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
};

export default App;
